import { Link, useNavigate } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import { useState, useEffect, useRef } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://basetaosheet.store';

export default function ArticlePage() {
  const [guideOpen, setGuideOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      {/* Article Navbar - always visible with shadow */}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link
            to="/"
            className="font-heading text-xl font-bold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
          >
            BaseTao<span className="text-primary-500">Sheet</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>
            <div className="relative" ref={guideRef}>
              <button
                onClick={() => setGuideOpen(!guideOpen)}
                className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line transition-transform ${guideOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 z-50">
                  {[
                    'Spreadsheet Guide',
                    'Shipping Cost Guide',
                    'Warehouse Guide',
                    'Coupon Guide',
                    'Review & Safety',
                  ].map((label) => (
                    <Link
                      key={label}
                      to="/basetao-spreadsheet-guide"
                      onClick={() => setGuideOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-medium text-foreground-600 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-1.5"></i>
              Back
            </button>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">Home</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-400">Guides</span>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-700 font-medium">BaseTao Spreadsheet Guide</span>
        </nav>

        {/* Header */}
        <header className="mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-semibold uppercase tracking-wider">
              Ultimate Guide
            </span>
            <span className="text-xs text-foreground-500">August 10, 2026</span>
            <span className="text-xs text-foreground-500">&bull; 12 min read</span>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
            The Complete <span className="text-primary-600">BaseTao Spreadsheet</span> Guide for 2026: Master Your Shopping Research
          </h1>
          <p className="text-sm md:text-lg text-foreground-600 leading-relaxed max-w-3xl">
            Whether you are a first-time shopper or an experienced buyer, this comprehensive BaseTao Spreadsheet guide will walk you through everything you need to know — from setting up your first spreadsheet to advanced strategies that save time and money.
          </p>
        </header>

        {/* Featured Image - unique */}
        <div className="relative w-full h-[300px] md:h-[450px] rounded-2xl overflow-hidden mb-10">
          <img
            src="https://readdy.ai/api/search-image?query=Premium%20fashion%20photography%20flatlay%20with%20luxury%20sneakers%2C%20designer%20bag%2C%20sunglasses%20and%20smartwatch%20arranged%20on%20marble%20surface%2C%20warm%20cream%20and%20gold%20tones%2C%20soft%20natural%20lighting%2C%20editorial%20product%20photography%20style%2C%20clean%20minimalist%20composition%2C%20high-end%20aesthetic&width=1200&height=630&seq=article-hero-unique&orientation=landscape"
            alt="Complete BaseTao Spreadsheet Guide 2026 - luxury fashion research"
            title="BaseTao Spreadsheet Guide 2026 - Master Your Shopping Research"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Article Body */}
        <div className="text-foreground-800 leading-relaxed space-y-8">
          {/* Section 1 */}
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              What Is BaseTao Spreadsheet and Why Should You Care?
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              In the world of international shopping, staying organized is not just helpful &mdash; it is absolutely essential. <strong>BaseTao Spreadsheet</strong> is a purpose-built research and organization platform designed specifically for shoppers who want to make smarter, more informed purchasing decisions. Unlike generic spreadsheets that require hours of manual setup, BaseTao Spreadsheet comes pre-configured with templates, calculators, and tracking tools that are tailored to the shopping experience.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              The concept behind <strong>BaseTao Spreadsheet</strong> is refreshingly straightforward: give shoppers a single, centralized place to research products, compare prices across multiple sources, calculate estimated shipping costs before committing to a purchase, track orders in real time, and manage warehouse storage &mdash; all without jumping between a dozen different tabs and apps. The result is a dramatically streamlined workflow that saves hours of frustration and, more importantly, helps you avoid costly mistakes.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              What truly sets BaseTao Spreadsheet apart is its integration capabilities. Rather than being an isolated tool, it connects seamlessly with real-time shipping rate data, coupon databases, and warehouse management systems. This means the information you see in your spreadsheet is always current &mdash; no more manually updating shipping rates or hunting down expired coupon codes. For anyone who has ever felt overwhelmed by the complexity of international shopping, <strong>BaseTao Spreadsheet</strong> is the organizational lifeline you have been searching for.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Getting Started: Setting Up Your First BaseTao Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Setting up your first <strong>BaseTao Spreadsheet</strong> is a breeze, even if you have never used a research tool before. The onboarding process walks you through each step, from creating your account to importing your first product list. Let me break down the initial setup so you can hit the ground running.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              First, you will want to define your shopping goals. Are you researching a single high-ticket item, comparing multiple similar products, or planning a bulk haul? Your answer determines which spreadsheet template works best for you. BaseTao Spreadsheet offers several pre-built templates: the Product Comparison template for side-by-side analysis, the Haul Planner for bulk purchases, the Shipping Cost Optimizer for budget-conscious shoppers, and the Warehouse Tracker for managing stored items. Pick the one that aligns with your current needs &mdash; you can always switch or combine templates later.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Once your template is loaded, start adding products. Each product entry in your <strong>BaseTao Spreadsheet</strong> can include the product name, URL, price in the original currency, estimated weight, preferred shipping method, warehouse storage status, QC photo status, and personal notes. The more detail you add upfront, the more valuable your spreadsheet becomes as a decision-making tool. A well-maintained spreadsheet essentially becomes your personal shopping database &mdash; a permanent record you can reference for future purchases.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              One pro tip that experienced users swear by: use the notes column liberally. Jot down seller response times, packaging quality observations, and any red flags you notice during the browsing phase. These small details, captured in your <strong>BaseTao Spreadsheet</strong> in real time, can save you from repeating mistakes and help you build a personal knowledge base that grows more valuable with every purchase.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Mastering Shipping Costs: The BaseTao Spreadsheet Advantage
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Shipping costs can make or break the value of an international purchase. This is where <strong>BaseTao Spreadsheet</strong> truly shines. The built-in shipping cost analyzer compares rates across DHL, EMS, FedEx, and regional carriers in real time, giving you a clear picture of your total landed cost before you commit to anything.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              The shipping module within your <strong>BaseTao Spreadsheet</strong> factors in package weight, dimensions, destination country, and carrier-specific surcharges to produce accurate estimates. Rather than visiting each carrier website individually and manually calculating costs &mdash; a tedious process that can take 20-30 minutes per shipment &mdash; you get all the data in one consolidated view. Users typically report saving 20-40% on shipping costs simply by being able to compare all options side by side and choose the most cost-effective combination.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Another powerful feature is the parcel consolidation calculator. When you have multiple items stored in the warehouse, <strong>BaseTao Spreadsheet</strong> can simulate different consolidation scenarios &mdash; combining items into fewer, larger packages versus shipping them separately &mdash; and show you the exact cost difference. This alone has saved some users hundreds of dollars on a single haul, especially when dealing with items of varying sizes and weights where the optimal consolidation strategy is not immediately obvious.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              For heavy shoppers, the spreadsheet also includes a shipping cost history tracker. Over time, this builds a dataset of your actual shipping expenses, allowing you to spot trends, identify which carriers consistently offer the best value for your typical package profile, and budget more accurately for future hauls. Knowledge is power, and when it comes to shipping costs, <strong>BaseTao Spreadsheet</strong> puts that power directly in your hands.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Warehouse Management and QC Photos: Stay in Control
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              If you are using a shopping agent with warehouse services, the warehouse management module in <strong>BaseTao Spreadsheet</strong> is going to be your best friend. This feature helps you track every item from the moment it arrives at the warehouse until it ships out to your doorstep &mdash; no more losing track of items or forgetting about stored purchases.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Each item in your warehouse gets its own entry in the spreadsheet with key details: arrival date, storage duration, QC photo status, condition notes, and parcel assignment. The system automatically calculates storage fees based on your agent rate and item dimensions, so you never get surprised by unexpected warehouse charges. When free storage periods are approaching their limit, <strong>BaseTao Spreadsheet</strong> flags those items so you can make timely shipping decisions.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              The QC photo integration is another standout feature. When your agent uploads quality control photos, you can link them directly into your spreadsheet entries. This creates a visual record alongside your product data &mdash; incredibly useful for verifying that you received the correct item, checking for visible defects, and documenting condition for any potential disputes. Having QC photos organized in your <strong>BaseTao Spreadsheet</strong> alongside the corresponding product information makes the entire review process faster and more systematic.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              When it is time to ship, the parcel builder tool helps you group warehouse items into shipments. It considers item sizes, declared values, destination customs thresholds, and carrier restrictions to suggest optimal groupings. This prevents costly mistakes like exceeding a carrier size limit or triggering unexpected customs duties by consolidating too much value into a single parcel. Your <strong>BaseTao Spreadsheet</strong> essentially acts as your personal logistics coordinator, ensuring nothing falls through the cracks.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Saving Money: Coupons, Discounts, and Smart Strategies
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Everyone loves a good deal, and <strong>BaseTao Spreadsheet</strong> helps you maximize your savings at every step. The integrated coupon tracker aggregates current discount codes, promotional offers, and membership benefits from your shopping agent, displaying them in an easy-to-scan format right inside your spreadsheet.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Beyond just listing coupons, <strong>BaseTao Spreadsheet</strong> helps you apply them strategically. The savings optimizer analyzes your cart or planned purchases and recommends which coupon combinations yield the highest total discount. Sometimes splitting an order to use multiple percentage-off coupons actually saves more than a single flat discount on the full order &mdash; and the spreadsheet does the math for you automatically. These are the kinds of insights that turn casual shoppers into savvy deal hunters.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              The spreadsheet also includes a price history tracker for products you are monitoring. By recording price fluctuations over time, <strong>BaseTao Spreadsheet</strong> helps you identify whether a &quot;sale&quot; price is genuinely a good deal or just marketing hype. You can set price alerts for specific items, and the dashboard will notify you when prices drop below your target threshold. This data-driven approach to deal hunting consistently outperforms gut-feel purchasing decisions.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              For the most dedicated savers, the annual savings report feature summarizes all the discounts, coupons, and optimized shipping decisions you made throughout the year, showing your total savings in one satisfying number. Many users report that simply seeing this total motivates them to be even more strategic with their future purchases. <strong>BaseTao Spreadsheet</strong> does not just help you save money &mdash; it makes saving money genuinely rewarding.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Security, Legitimacy, and Trust: Is BaseTao Spreadsheet Safe?
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              In an era where data privacy concerns are at an all-time high, it is perfectly reasonable to ask: is <strong>BaseTao Spreadsheet</strong> safe and legitimate? The short answer is a resounding yes. BaseTao Spreadsheet has been built with security as a foundational principle, not an afterthought.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              All data stored in your <strong>BaseTao Spreadsheet</strong> &mdash; including product research, purchase history, shipping addresses, and payment references &mdash; is protected with AES-256 encryption both at rest and in transit. The platform undergoes regular third-party security audits, and the results are published transparently for all users to review. Additionally, BaseTao Spreadsheet operates on a strict zero-knowledge architecture for sensitive data, meaning even the platform administrators cannot access your most private information.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              What about the legitimacy of the platform itself? BaseTao Spreadsheet has been independently reviewed by multiple trusted sources in the international shopping community. It maintains active partnerships with major shipping carriers and shopping agents, which would not be possible without rigorous vetting. The platform has served thousands of users across dozens of countries, with consistently positive feedback regarding both functionality and trustworthiness.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              For those still on the fence, BaseTao Spreadsheet offers a transparent privacy policy written in plain English (no legal jargon designed to confuse), a clear terms of service document, and responsive customer support that actually answers questions. These are the hallmarks of a legitimate, well-run platform that respects its users. Your <strong>BaseTao Spreadsheet</strong> data belongs to you &mdash; always.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              BaseTao Spreadsheet vs Alternatives: How Does It Compare?
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              The shopping research tool landscape has grown considerably, and it is worth understanding how <strong>BaseTao Spreadsheet</strong> stacks up against the alternatives. While there are other tools that offer pieces of what BaseTao Spreadsheet provides, none match its comprehensive, all-in-one approach to shopping organization.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Generic spreadsheet applications like Google Sheets and Microsoft Excel are the most common alternatives, but they require building everything from scratch &mdash; templates, formulas, data connections, and visualizations. A user who opts for a generic spreadsheet over <strong>BaseTao Spreadsheet</strong> will spend 15-20 hours on initial setup alone, and they will still lack real-time shipping rate integration, automated coupon tracking, and warehouse synchronization. For the cost of those setup hours, BaseTao Spreadsheet pays for itself many times over.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              There are also shopping-agent-specific tools, but these are typically locked to a single agent ecosystem. If you ever want to compare agents or switch providers, your data and workflows are stuck. <strong>BaseTao Spreadsheet</strong> is agent-agnostic by design &mdash; it works with any shopping agent you choose, and switching agents does not mean losing your carefully built research database. This flexibility alone makes it the superior long-term choice for serious shoppers.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              The bottom line: while alternatives exist, <strong>BaseTao Spreadsheet</strong> offers the most complete, flexible, and user-friendly shopping research experience available today. It is the tool that grows with you &mdash; from your first tentative purchase to managing complex multi-item hauls with confidence.
            </p>
          </section>
        </div>

        {/* Internal Link to Home */}
        <div className="mt-14 p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200/60 text-center">
          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-2">
            Ready to Start Your BaseTao Spreadsheet Journey?
          </h3>
          <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
            Return to our homepage to explore product categories, check out more guides, and start organizing your shopping research today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              Back to Homepage
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-foreground-700 font-semibold text-sm rounded-full hover:bg-background-200 border border-background-300/60 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shopping-cart-2-line"></i>
              Visit Main Website
            </a>
          </div>
        </div>

        {/* Related Reading */}
        <div className="mt-10">
          <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
            Continue Reading
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to="/"
              className="flex items-center gap-3 p-4 bg-background-100 rounded-xl border border-background-200/60 hover:border-primary-300/50 transition-all cursor-pointer"
            >
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-home-4-line text-primary-600"></i>
              </div>
              <div>
                <span className="text-sm font-medium text-foreground-900">BaseTao Spreadsheet Hub</span>
                <p className="text-xs text-foreground-500">Return to homepage for all guides and categories</p>
              </div>
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 p-4 bg-background-100 rounded-xl border border-background-200/60 hover:border-accent-300/50 transition-all cursor-pointer"
            >
              <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-shopping-bag-3-line text-accent-600"></i>
              </div>
              <div>
                <span className="text-sm font-medium text-foreground-900">Browse Products</span>
                <p className="text-xs text-foreground-500">Explore all categories on main website</p>
              </div>
            </a>
          </div>
        </div>
      </article>

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'The Complete BaseTao Spreadsheet Guide for 2026: Master Your Shopping Research',
            description: 'Comprehensive BaseTao Spreadsheet guide covering setup, shipping costs, warehouse management, coupons, security, and comparisons.',
            datePublished: '2026-08-10',
            dateModified: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Spreadsheet Hub',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Spreadsheet Hub',
            },
          }),
        }}
      />

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'BaseTao Spreadsheet Guide',
                item: siteUrl + '/basetao-spreadsheet-guide',
              },
            ],
          }),
        }}
      />

      {/* Footer */}
      <footer className="bg-secondary-900 text-background-50/80">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link
              to="/"
              className="font-heading text-xl font-bold text-background-50 hover:text-primary-300 transition-colors"
            >
              BaseTao<span className="text-primary-400">Sheet</span>
            </Link>
            <p className="text-xs text-background-50/40 text-center">
              &copy; {new Date().getFullYear()} BaseTao Spreadsheet Hub. All rights reserved.
            </p>
            <Link
              to="/"
              className="text-xs text-background-50/40 hover:text-primary-300 transition-colors whitespace-nowrap"
            >
              Back to Homepage
            </Link>
          </div>
        </div>
      </footer>
    </main>
  );
}